import React from 'react'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import {
  GetReportDetailsQuery,
  ReportFavoriteElement,
  useGetReportElementsQuery,
  useGetReportFavoriteElementsQuery,
} from 'gqlTypes'
import { Box, Grid, Typography } from '@mui/material'
import ReportElement from './ReportElement/ReportElement'
import ReportElementFavorite from './ReportElement/ReportElementFavorite'
import { getCurrentSection, getParentSection } from 'util/getSection'

const ReportElementsSkeleton = () => (
  <Paper sx={{ width: '100%', p: 4 }}>
    <Stack spacing={1}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Skeleton key={i} variant="text" />
      ))}
      <Skeleton variant="rounded" height={200} />
    </Stack>
  </Paper>
)

interface Props {
  report: NonNullable<GetReportDetailsQuery['report']>
  sectionId: string
  onUpdateFavorites: () => void
}
const ReportElements: React.FC<Props> = ({
  report,
  sectionId,
  onUpdateFavorites,
}) => {
  const reportId = report.id
  const { data, refetch, loading } = useGetReportElementsQuery({
    variables: { reportId, sectionId },
  })
  const {
    data: favoriteData,
    loading: favoriteLoading,
    refetch: favoriteRefetch,
  } = useGetReportFavoriteElementsQuery({
    variables: {
      reportId: reportId,
      sectionId: sectionId,
    },
  })

  const currentSection = getCurrentSection(report.sections, sectionId)
  const parentSection = getParentSection(
    report.sections,
    currentSection?.parentReportSectionId
  )

  const handleUpdateFavorites = () => {
    refetch()
    favoriteRefetch()
    onUpdateFavorites()
  }

  if (loading) {
    return <ReportElementsSkeleton />
  }

  return (
    <Paper sx={{ width: '100%' }}>
      <Grid
        container
        p={4}
        pt={2}
        direction={{
          xs: 'column-reverse',
          sm: 'column-reverse',
          md: 'column-reverse',
          lg: 'row',
          xl: 'row',
        }}
      >
        <Grid
          item
          xs={12}
          sm={12}
          md={12}
          lg={9}
          display="flex"
          alignContent="center"
          alignItems="center"
        >
          <Typography
            component="small"
            sx={{ color: '#72828E', fontSize: '0.9rem' }}
          >
            {report.title}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={3}>
          {currentSection && (
            <Box display="flex" justifyContent={'flex-end'}>
              <ReportElementFavorite
                id={currentSection.id}
                type={ReportFavoriteElement.Section}
                report={report}
                sectionId={sectionId}
                onUpdate={handleUpdateFavorites}
              />
            </Box>
          )}
        </Grid>
      </Grid>
      <Box px={4} mb={4}>
        {parentSection && (
          <Typography variant="h5" color="secondary">
            {parentSection.title}
          </Typography>
        )}
        {currentSection && (
          <Typography variant="h2">{currentSection.title}</Typography>
        )}
      </Box>
      {currentSection &&
        data?.reportElements.map((element) => {
          return (
            <Box
              key={element.id}
              sx={{ display: 'flex', width: '100%', overflow: 'hidden' }}
            >
              <ReportElement
                reportId={report.id}
                report={report}
                element={element}
                sectionId={sectionId}
                onUpdateFavorites={handleUpdateFavorites}
              />
            </Box>
          )
        })}
    </Paper>
  )
}

export default ReportElements
